import React, { useState } from 'react';
import { Sparkles, Gift, ArrowRight, CheckCircle2, PartyPopper, Heart, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';

const initial = { nombre: '', correo: '', comuna: '', edad: '', telefono: '', privacidad: false };
const fieldStyle = "w-full h-12 rounded-xl bg-[#f6f6f1] border-2 border-transparent focus:border-[#ff5b83] outline-none px-4 text-[#171717] placeholder:text-[#777] text-base transition-colors";

export default function SeniorMarketQR() {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const set = (key) => (event) => setForm((old) => ({...old, [key]: event.target.value}));
  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true); setMessage('');
    try {
      await api.post('/seniormarket/registro', { ...form, edad: Number(form.edad) });
      setSuccess(true);
    } catch (err) {
      if (err.response?.status === 409) setSuccess(true);
      else {
        const detail = err.response?.data?.detail;
        setMessage(typeof detail === 'string' ? detail : 'No pudimos guardar tu registro. Revisa tus datos e inténtalo nuevamente.');
      }
    } finally { setSaving(false); }
  };

  return (
    <main className="min-h-[100dvh] bg-[#ff5a83] text-[#171717] relative overflow-hidden flex items-center justify-center px-4 py-5 sm:py-8">
      <div aria-hidden="true" className="absolute w-72 h-72 rounded-full bg-[#ff5b83] -bottom-44 -right-28" />
      <div className="relative w-full max-w-[570px]">
        <div className="text-center mb-3">
          <div className="inline-flex items-center gap-2 bg-[#111] text-[#dfff00] rounded-full px-4 py-1.5 text-[11px] font-extrabold tracking-[.15em] uppercase">
            <Sparkles size={14} /> 07 NOV · MERCADO URBANO <Sparkles size={14} />
          </div>
          <div className="mt-3 flex justify-center">
            <img
              src="/seniormarket-logos/seniormarket-brand-qr-transparente.png"
              alt="Senior Market"
              className="block w-full max-w-[285px] sm:max-w-[340px] h-auto object-contain"
              width="492"
              height="250"
            />
          </div>
        </div>
        <section className="bg-white rounded-[27px] shadow-[9px_9px_0_#111] p-5 sm:p-7 border-[3px] border-[#111]">
          {success ? (
            <div className="py-10 sm:py-12 text-center flex flex-col items-center gap-4" role="status">
              <div className="bg-[#dfff00] rounded-full p-6 border-[3px] border-[#111]"><PartyPopper size={52} /></div>
              <h1 className="text-4xl sm:text-5xl font-black leading-[.99] uppercase">¡YA ESTÁS<br/>PARTICIPANDO!</h1>
              <p className="text-lg font-semibold max-w-xs">Al final de nuestro Mercado Urbano entregaremos premios entre las personas registradas. ¡Mucha suerte!</p>
              <span className="text-sm font-bold bg-[#08d7ee] rounded-full px-5 py-2">¡Gracias por ser parte! 💛</span>
            </div>
          ) : (
            <>
              <div className="text-center mb-4">
                <div className="inline-flex bg-[#08d7ee] px-3 py-1 -rotate-2 text-xs font-black uppercase tracking-wider">¡Regístrate y participa!</div>
                <h1 className="font-black text-[29px] sm:text-[36px] tracking-tight uppercase leading-[1.03] mt-2">¡TENEMOS <span className="text-[#f23c70]">PREMIOS</span><br/>PARA TI! <span aria-hidden="true">🎁</span></h1>
                <p className="text-[13px] sm:text-sm font-medium mt-1 text-[#555]">Completa tus datos y participa por premios que entregaremos al finalizar nuestro Mercado Urbano.</p>
              </div>
              <form onSubmit={submit} className="space-y-2.5">
                <label className="block"><span className="block text-xs font-extrabold mb-1">Nombre completo *</span><input aria-label="Nombre completo" className={fieldStyle} autoComplete="name" name="nombre" placeholder="Tu nombre y apellido" required minLength={3} maxLength={120} value={form.nombre} onChange={set('nombre')}/></label>
                <label className="block"><span className="block text-xs font-extrabold mb-1">Correo electrónico *</span><input aria-label="Correo electrónico" className={fieldStyle} type="email" autoComplete="email" name="correo" placeholder="tunombre@correo.cl" required maxLength={254} value={form.correo} onChange={set('correo')}/></label>
                <div className="grid grid-cols-2 gap-2.5">
                  <label className="block"><span className="block text-xs font-extrabold mb-1">Comuna *</span><input aria-label="Comuna" className={fieldStyle} autoComplete="address-level2" name="comuna" placeholder="Tu comuna" required minLength={2} maxLength={100} value={form.comuna} onChange={set('comuna')}/></label>
                  <label className="block"><span className="block text-xs font-extrabold mb-1">Edad *</span><input aria-label="Edad" className={fieldStyle} type="number" inputMode="numeric" name="edad" placeholder="Tu edad" required min={18} max={110} value={form.edad} onChange={set('edad')}/></label>
                </div>
                <label className="block"><span className="block text-xs font-extrabold mb-1">Teléfono *</span><input aria-label="Teléfono" className={fieldStyle} type="tel" inputMode="tel" autoComplete="tel" name="telefono" placeholder="+56 9 1234 5678" required minLength={8} maxLength={20} value={form.telefono} onChange={set('telefono')}/></label>
                <label className="flex items-start gap-2.5 text-[11px] text-[#555] cursor-pointer pt-1">
                  <input type="checkbox" className="mt-0.5 accent-[#111] w-4 h-4 shrink-0" checked={form.privacidad} onChange={(e) => setForm(old => ({...old, privacidad: e.target.checked}))} required/>
                  <span>Acepto el uso de mis datos para administrar este registro y contactar a quienes resulten seleccionados. <a className="underline font-semibold" href="/privacidad" target="_blank" rel="noopener noreferrer">Política de privacidad</a>. Solo mayores de 18 años.</span>
                </label>
                {message && <p role="alert" className="text-red-700 font-semibold text-xs">{message}</p>}
                <button type="submit" disabled={saving} className="w-full min-h-12 rounded-xl bg-[#111] text-white hover:bg-[#f23c70] disabled:opacity-60 transition-colors flex items-center justify-center gap-2 font-black text-base uppercase tracking-wider">
                  {saving ? 'Guardando...' : '¡QUIERO PARTICIPAR!'} <ArrowRight size={20}/>
                </button>
              </form>
            </>
          )}
        </section>
        <div className="text-center mt-4 flex justify-center items-center gap-2 text-xs font-black"><Heart size={15} fill="#f23c70" stroke="#f23c70" /> SENIORMARKET · VIVAMOS EL MERCADO URBANO</div>
      </div>
    </main>
  );
}
